import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowUpRight, BookOpen, Key, Plus } from 'lucide-react';
import { CHART } from '@/lib/chartColors';
import { useAccountBalance, useTokens, useUsageSummary } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import { formatCompact, formatNumber } from '@/lib/utils';
import { DOCS_URL } from '@/lib/constants';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Segmented } from '@/components/ui/Segmented';
import { StatTile } from '@/components/ui/StatTile';
import { ChartTooltip } from '@/components/charts/ChartTooltip';

type Days = 7 | 30 | 90;
type Metric = 'requests' | 'credits';

const PERIODS: { value: Days; label: string }[] = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];
/** One credit is ₦0.10. */
const naira = (credits: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 }).format(credits / 10);

function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}
const shortDate = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [days, setDays] = useState<Days>(30);
  const [metric, setMetric] = useState<Metric>('requests');

  // The window is fixed when the period changes, so the query key is stable between renders.
  const range = useMemo(() => {
    const end = Math.floor(Date.now() / 1000);
    return { start: end - days * 86400, end };
  }, [days]);
  const { data: usage, isLoading: usageLoading } = useUsageSummary(range);
  const { data: balance, isLoading: balanceLoading } = useAccountBalance();
  const { data: tokens, isLoading: tokensLoading } = useTokens();

  const firstName = (user?.name ?? '').trim().split(/\s+/)[0];
  const byDay = usage?.by_day ?? [];
  const products = Object.entries(usage?.by_service ?? {})
    .map(([name, s]) => ({ name, requests: s.requests, credits: s.credits }))
    .sort((a, b) => b.requests - a.requests);
  const busiest = products[0]?.requests ?? 0;
  const noUsage = !usageLoading && (usage?.total_requests ?? 0) === 0;
  const activeKeys = tokens?.filter((t) => t.is_active).length ?? 0;

  // Free monthly allowances start again on the 1st (UTC).
  const now = new Date();
  const daysUntilReset = Math.ceil((Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1) - now.getTime()) / 86400000);

  return (
    <div className="space-y-6">
      <PageHeader
        title={firstName ? `${greeting()}, ${firstName}` : greeting()}
        description={`Your API usage over the last ${days} days.`}
        actions={
          <>
            <Segmented label="Period" value={days} options={PERIODS} onChange={setDays} />
            <Link to="/keys" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-accent px-4 text-[13px] font-medium text-[#0B0C0E] transition-transform hover:brightness-105 active:scale-[0.98]">
              <Plus className="h-4 w-4" /> New API key
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Requests" value={formatNumber(usage?.total_requests ?? 0)} hint={`Last ${days} days`} trend={byDay.map((d) => d.requests)} loading={usageLoading} />
        <StatTile label="Credits used" value={formatNumber(usage?.total_credits_used ?? 0)} hint={naira(usage?.total_credits_used ?? 0)} trend={byDay.map((d) => d.credits)} loading={usageLoading} />
        <StatTile label="Error rate" value={`${(usage?.error_rate ?? 0).toFixed(1)}%`} hint="Failed calls cost nothing" loading={usageLoading} />
        <StatTile
          label="Median latency"
          value={usage?.median_response_time_ms == null ? 'None yet' : `${Math.round(usage.median_response_time_ms)} ms`}
          hint="Time to answer, at the gateway"
          loading={usageLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <Panel
          className="flex flex-col"
          bodyClassName="flex flex-1 flex-col"
          title="Activity"
          action={
            <Segmented
              label="Chart"
              value={metric}
              options={[{ value: 'requests', label: 'Requests' }, { value: 'credits', label: 'Credits' }]}
              onChange={setMetric}
            />
          }
        >
          {usageLoading ? (
            <div className="skeleton min-h-72 flex-1" aria-hidden="true" />
          ) : noUsage ? (
            <div className="flex min-h-72 flex-1 flex-col items-start justify-center gap-3 rounded-xl border border-dashed border-border px-6">
              <p className="text-base font-medium text-foreground">Make your first request</p>
              <p className="max-w-[48ch] text-sm text-muted-foreground">
                Create an API key, then call search, routing or map tiles. Your requests show up here within a minute, and
                every product has a free monthly allowance.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Link to="/keys" className="inline-flex h-9 items-center gap-1.5 rounded-full bg-accent px-4 text-[13px] font-medium text-[#0B0C0E]">
                  <Key className="h-4 w-4" /> Create an API key
                </Link>
                <a href={`${DOCS_URL}/quickstart/`} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-4 text-[13px] font-medium text-foreground hover:bg-surface-raised">
                  <BookOpen className="h-4 w-4" /> Read the quickstart
                </a>
              </div>
            </div>
          ) : (
            <div className="relative min-h-72 flex-1">
              <ResponsiveContainer width="100%" height="100%" className="absolute inset-0">
                <AreaChart data={byDay} margin={{ top: 8, right: 4, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="activityFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CHART.primary} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={CHART.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={shortDate} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tickFormatter={formatCompact} tickLine={false} axisLine={false} width={48} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} labelFormatter={shortDate} cursor={{ stroke: 'rgb(var(--color-border))' }} />
                  <Area
                    type="monotone"
                    dataKey={metric}
                    name={metric === 'requests' ? 'Requests' : 'Credits'}
                    stroke={CHART.primary}
                    strokeWidth={2}
                    fill="url(#activityFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <div className="grid gap-4">
          <Panel title="Credit balance">
            {balanceLoading ? (
              <div aria-hidden="true"><div className="skeleton h-9 w-32" /><div className="skeleton mt-3 h-3 w-40" /></div>
            ) : (
              <>
                <p className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">{formatNumber(balance?.prepaid_balance ?? 0)}</p>
                <p className="mt-1 text-sm text-muted-foreground">credits, worth <span className="text-foreground">{naira(balance?.prepaid_balance ?? 0)}</span></p>
                <p className="mt-3 text-xs text-muted-foreground">Free allowances reset in {daysUntilReset} {daysUntilReset === 1 ? 'day' : 'days'}</p>
                <Link to="/billing" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline">
                  Add credits <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </>
            )}
          </Panel>

          <Panel title="By product" action={<Link to="/usage" className="text-xs font-medium text-accent-text hover:underline">Details</Link>}>
            {usageLoading ? (
              <div className="space-y-3" aria-hidden="true">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-6" />)}</div>
            ) : products.length ? (
              <ul className="space-y-3">
                {products.slice(0, 5).map((p) => (
                  <li key={p.name}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate text-foreground">{p.name}</span>
                      <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatNumber(p.requests)}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-surface-raised">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${Math.max(4, (p.requests / busiest) * 100)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nothing yet. Each product you call appears here.</p>
            )}
          </Panel>
        </div>
      </div>

      <Panel title="API keys" action={<Link to="/keys" className="text-xs font-medium text-accent-text hover:underline">Manage keys</Link>} bodyClassName="px-0 pb-2">
        {tokensLoading ? (
          <div className="space-y-2 px-5 pb-3" aria-hidden="true">{[0, 1].map((i) => <div key={i} className="skeleton h-10" />)}</div>
        ) : tokens?.length ? (
          <>
            <ul className="divide-y divide-border">
              {tokens.slice(0, 5).map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.last_used_at ? `Last used ${new Date(t.last_used_at * 1000).toLocaleDateString('en-GB')}` : 'Never used'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">{t.token_prefix}…</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${t.environment === 'production' ? 'bg-accent/10 text-accent-text' : 'bg-surface-raised text-muted-foreground'}`}>
                      {t.environment === 'production' ? 'Production' : 'Sandbox'}
                    </span>
                    {!t.is_active && <span className="text-[11px] text-muted-foreground">Revoked</span>}
                  </div>
                </li>
              ))}
            </ul>
            <p className="px-5 pt-2 text-xs text-muted-foreground">{activeKeys} of {tokens.length} keys active</p>
          </>
        ) : (
          <div className="px-5 pb-3">
            <p className="text-sm text-muted-foreground">No keys yet. A sandbox key is free to use and costs no credits.</p>
            <Link to="/keys" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline">Create your first key</Link>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default DashboardPage;
